import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcPaymentsV1WireQueryServiceWireGet } from '@interxfi/api-client/generated/hooks/ixc-payments-v1-wire-query-service-wire-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { getReadonlyListItemPill } from '@interxfi/app-components/components/readonly-list/util';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcPaymentsV1WireState } from '@interxfi/types/generated/api';
import { FundingTransferTypePill } from '../../../../../../../../../../features/funding/transfer/funding-transfer-type-pill';
import { PaymentsWireEntityMenu } from '../../../../../../../../../../features/payments/wire/payments-wire-entity-menu';
import { PaymentsWireEventSummary } from '../../../../../../../../../../features/payments/wire/payments-wire-event';
import { getPaymentsWireStateListItems } from '../../../../../../../../../../features/payments/wire/payments-wire-list-items';
import { getPaymentsWireDetailPath } from '../../../../../../../../../../features/payments/wire/payments-wire-routing';
import { PaymentsWireStatusPill } from '../../../../../../../../../../features/payments/wire/payments-wire-status-pill';
import { PaymentsWireSummaryBar } from '../../../../../../../../../../features/payments/wire/payments-wire-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/finance/_finance/payment/wire/_wire/$wireId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `wire-payment-${params.wireId}`,
        label: 'Wire',
        to: getPaymentsWireDetailPath(params.wireId),
        suffix: <CopyButton content={params.wireId} label="Copy Wire ID" tooltip={{ copy: params.wireId, copied: 'Copied Wire ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Wire Payment'),
  component: PaymentsWireDetail,
});

function getListItems(data: IxcPaymentsV1WireState | undefined) {
  const items = getPaymentsWireStateListItems(data);

  return {
    general: {
      left: [
        {
          id: 'main',
          items: [items.data.spec.amount],
        },
        {
          id: 'transfer',
          title: 'Transfer',
          items: [
            getReadonlyListItemPill(
              'transferType',
              'Type',
              data?.data?.spec?.wirePush ? <FundingTransferTypePill type={{ '!type': 'wirePush', wirePush: data.data.spec.wirePush }} /> : null,
            ),
            { ...items.data.spec.wirePush.beneficiary.name, title: 'Beneficiary Name' },
            { ...items.data.spec.wirePush.beneficiary.address.formatted, title: 'Beneficiary Address' },
            ...(items.data.spec.wirePush.originator
              ? [
                  { ...items.data.spec.wirePush.originator.name, title: 'Originator Name' },
                  { ...items.data.spec.wirePush.originator.address.formatted, title: 'Originator Address' },
                ]
              : []),
            ...(items.data.spec.wirePush.messageToRecipient ? [items.data.spec.wirePush.messageToRecipient] : []),
          ],
        },
        {
          id: 'source',
          title: 'Source',
          items: [
            items.data.spec.domesticWireSource.bankDetails.routingNumber,
            items.data.spec.domesticWireSource.bankDetails.accountNumber,
            { ...items.data.spec.domesticWireSource.bankDetails.accountHolderName, title: 'Name on Account' },
            items.data.spec.domesticWireSource.bankDetails.accountType,
          ],
        },
      ] satisfies ReadonlyListGroup[],
      right: [
        items.metadata.createdAt,
        items.metadata.updatedAt,
        items.wireId,
        items.data.spec.sourceId,
        items.data.spec.transferId,
        items.data.spec.accountId,
        items.data.spec.programId,
      ] satisfies ReadonlyListItem[],
    },
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load wire', 'view this wire');

function PaymentsWireDetail() {
  const { wireId } = Route.useParams();
  const request = { wireId } as const;
  const { data, error, isPending } = useIxcPaymentsV1WireQueryServiceWireGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.wire), [data?.wire]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <PaymentsWireStatusPill isPending={showPending} size="large" status={data?.wire?.status} />
          </HorizontalStack>

          <PaymentsWireEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <PaymentsWireSummaryBar {...request} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <PaymentsWireEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.general.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.general.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
